import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { normalizeBaskiGorselUrls, resolveBaskiGorselUrl } from '../../utils/baskiGorselUtils';
import { BaskiGorselComponent } from './BaskiGorselLazyImage';

/**
 * Desen görselleri galerisi — kartta tek thumbnail, tıklanınca tüm resimler.
 */
const BaskiGorselGalleryModal = ({
  isOpen,
  onClose,
  title = 'Desen görselleri',
  gorselUrls = [],
  initialIndex = 0,
  zIndexClass = 'z-[70]'
}) => {
  const urls = useMemo(() => normalizeBaskiGorselUrls(gorselUrls), [gorselUrls]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const safeIndex = Math.min(Math.max(0, initialIndex), Math.max(0, urls.length - 1));
    setActiveIndex(safeIndex);
  }, [isOpen, initialIndex, urls.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
        return;
      }
      if (event.key === 'ArrowLeft') {
        setActiveIndex((prev) => (prev - 1 + urls.length) % urls.length);
      }
      if (event.key === 'ArrowRight') {
        setActiveIndex((prev) => (prev + 1) % urls.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, urls.length]);

  if (!isOpen || urls.length === 0) return null;

  const activeUrl = resolveBaskiGorselUrl(urls[activeIndex]);
  const hasMany = urls.length > 1;

  const goPrev = (event) => {
    event?.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + urls.length) % urls.length);
  };

  const goNext = (event) => {
    event?.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % urls.length);
  };

  return (
    <div
      className={`fixed inset-0 ${zIndexClass} bg-black/80 flex items-center justify-center p-2 sm:p-4`}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
              {title}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Görsel {activeIndex + 1} / {urls.length}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-300"
            aria-label="Kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="relative flex-1 min-h-[240px] flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-3 sm:p-6">
          {hasMany && (
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-2 sm:left-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
              aria-label="Önceki görsel"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          <img
            src={activeUrl}
            alt={`${title} - ${activeIndex + 1}`}
            className="max-w-full max-h-[58vh] object-contain rounded-lg shadow-lg"
          />

          {hasMany && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 sm:right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
              aria-label="Sonraki görsel"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {hasMany && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-min">
              {urls.map((url, index) => (
                <button
                  key={`${url}-${index}`}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${
                    index === activeIndex
                      ? 'border-purple-500 ring-2 ring-purple-300'
                      : 'border-gray-200 dark:border-gray-600 opacity-80 hover:opacity-100'
                  }`}
                  aria-label={`Görsel ${index + 1}`}
                >
                  <BaskiGorselComponent
                    imageUrl={url}
                    alt={`${title} küçük ${index + 1}`}
                    useThumbnail
                    thumbnailSize={120}
                    priority={index === activeIndex}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BaskiGorselGalleryModal;
